import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";

import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { useSearchData } from "@/features/search/hooks";
import { saveRecentSearch } from "@/lib/recent-searches";
import SearchResults, { buildSections } from "@/features/search/components/SearchResults";

/**
 * DashboardSearch — barra de búsqueda INLINE del dashboard (Fase 2).
 *
 * Al escribir (≥2 letras), un panel de resultados aparece FLOTANDO debajo de la
 * barra (estilo Spotlight), anclado a ella, sobre el dashboard visible. NO abre
 * ningún modal. Reusa el motor (useSearchData) y la capa de presentación
 * (<SearchResults>) de la Fase 1.
 */
export default function DashboardSearch() {
  const navigate = useNavigate();
  const { districtId } = useDistrictGuard();

  const containerRef = useRef(null);
  const itemRefs     = useRef({});

  const [localQuery,       setLocalQuery]       = useState("");
  const [expandedSections, setExpandedSections] = useState(new Set());

  // ── Motor (reusado tal cual, sin tocar) ──────────────────────────────────
  const {
    query, setQuery, loading,
    filteredPeople, filteredFamilies, filteredChurches, filteredVisits,
    filteredEvents, filteredPrayers, filteredNotes, filteredPreaching,
    filteredMaterials, filteredBaptisms, matchedCommands, totalResults,
  } = useSearchData({ districtId });

  // ── Debounce 220ms (igual que el modal) ──────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setQuery(localQuery), 220);
    return () => clearTimeout(t);
  }, [localQuery, setQuery]);

  // ── Reset al cambiar query ───────────────────────────────────────────────
  useEffect(() => {
    setExpandedSections(new Set());
    itemRefs.current = {};
  }, [query]);

  // ── Secciones agrupadas (reusa buildSections de Fase 1) ──────────────────
  const sections = useMemo(
    () => query.trim()
      ? buildSections({ filteredPeople, filteredFamilies, filteredChurches, filteredVisits, filteredPrayers, filteredEvents, filteredNotes, filteredPreaching, filteredMaterials, filteredBaptisms })
      : [],
    [query, filteredPeople, filteredFamilies, filteredChurches, filteredVisits, filteredPrayers, filteredEvents, filteredNotes, filteredPreaching, filteredMaterials, filteredBaptisms]
  );
  const hasResults = sections.length > 0 || matchedCommands.length > 0;

  // ── Umbral de 3 letras: el panel solo aparece con query.trim().length >= 3 ─
  const showPanel = query.trim().length >= 3;

  // ── Limpiar / cerrar ─────────────────────────────────────────────────────
  const clear = useCallback(() => {
    setLocalQuery("");
    setQuery("");
  }, [setQuery]);

  // ── Navegar a un resultado → guarda reciente, limpia, navega ─────────────
  const handleNavigate = useCallback((route, recentItem) => {
    if (recentItem) saveRecentSearch(recentItem);
    clear();
    navigate(route);
  }, [navigate, clear]);

  // ── Click/tap fuera del panel → limpiar ──────────────────────────────────
  useEffect(() => {
    if (!showPanel) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) clear();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [showPanel, clear]);

  // ── Escape → limpiar ─────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && localQuery) clear(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [localQuery, clear]);

  return (
    <div className="px-4 mt-5">
      <div ref={containerRef} className="relative">

        {/* ═══ BARRA INPUT ═══ */}
        <div className="w-full flex items-center gap-3 px-4 h-[52px] rounded-[20px] border border-slate-200 dark:border-slate-700/60 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all duration-200 ease-out focus-within:border-blue-300 dark:focus-within:border-blue-500/50 focus-within:shadow-[0_4px_16px_rgba(0,102,255,0.10)]">
          <Search size={18} strokeWidth={2.2} className="text-[#1A7FFF] shrink-0" />
          <input
            type="text"
            value={localQuery}
            onChange={(e) => setLocalQuery(e.target.value)}
            placeholder="Buscar personas, hogares, visitas..."
            aria-label="Buscar"
            className="flex-1 min-w-0 bg-transparent text-[16px] text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none"
          />
          {localQuery ? (
            <button
              onClick={clear}
              aria-label="Limpiar búsqueda"
              className="shrink-0 h-7 w-7 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors active:scale-90"
            >
              <X size={15} strokeWidth={2.5} />
            </button>
          ) : (
            <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-[10px] text-slate-400 dark:text-slate-500 font-mono shrink-0">
              ⌘K
            </kbd>
          )}
        </div>

        {/* ═══ PANEL FLOTANTE de resultados (solo ≥2 letras) ═══ */}
        {showPanel && (
          <div className="anim-in absolute left-0 right-0 top-full mt-2 z-30 bg-[#f0f5fb] dark:bg-slate-900 rounded-[20px] border border-slate-200/60 dark:border-slate-700/50 shadow-[0_8px_30px_rgba(11,31,58,0.15)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.45)] overflow-hidden">
            <div className="max-h-[60vh] overflow-y-auto overscroll-contain pb-2">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#1A7FFF] border-t-transparent" />
                  <p className="mt-3 text-[13px] text-slate-400">Cargando datos...</p>
                </div>
              ) : (
                <SearchResults
                  showCommands
                  matchedCommands={matchedCommands}
                  visibleSections={sections}
                  query={query}
                  selectedIndex={-1}
                  expandedSections={expandedSections}
                  setExpandedSections={setExpandedSections}
                  itemRefs={itemRefs}
                  onHover={() => {}}
                  onNavigate={handleNavigate}
                  onPickHint={setLocalQuery}
                  hasResults={hasResults}
                  totalResults={totalResults}
                />
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
